import { Injectable } from '@angular/core';
import { EndpointService } from '../endpoints.service';
import { TRN_4, TRN_13, TRN_16, TRN_19 } from '../models/input.module';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TrainerService {

  constructor(private endpointService: EndpointService) {}

  /**
   * Get all active trainers (public) - TRN-1
   */
  getTrainers(params: { page?: number; per_page?: number; search?: string; specialization?: string } = {}) {
    return this.endpointService.trn_1(params).pipe(map(data => data));
  }

  /**
   * Get single trainer profile (public) - TRN-2
   */
  getTrainer(userId: string) {
    return this.endpointService.trn_2(userId).pipe(map(data => data.data));
  }

  /**
   * Create trainer subscription checkout - TRN-3
   * Returns checkout URL for Stripe
   */
  subscribe() {
    return this.endpointService.trn_3().pipe(map(data => data.data));
  }

  /**
   * Update trainer profile fields - TRN-4
   */
  updateProfile(params: TRN_4) {
    return this.endpointService.trn_4(params).pipe(map(data => data.data));
  }

  /**
   * Cancel trainer subscription - TRN-5
   */
  cancelSubscription() {
    return this.endpointService.trn_5().pipe(map(data => data.data));
  }

  /**
   * Check if current user is a trainer - TRN-6
   */
  getStatus() {
    return this.endpointService.trn_6().pipe(map(data => data.data));
  }

  /**
   * Get trainer's events (public) - TRN-7
   * Returns upcoming and past events where trainer is a contact
   */
  getTrainerEvents(userId: string) {
    return this.endpointService.trn_7(userId).pipe(map(data => data.data));
  }

  /**
   * Get trainer images (public) - TRN-8
   */
  getTrainerImages(userId: string) {
    return this.endpointService.trn_8(userId).pipe(map(data => data.data));
  }

  /**
   * Upload trainer image - TRN-9
   */
  uploadImage(image: File, caption?: string) {
    return this.endpointService.trn_9(image, caption).pipe(map(data => data.data));
  }

  /**
   * Delete trainer image - TRN-10
   */
  deleteImage(imageId: number) {
    return this.endpointService.trn_10(imageId).pipe(map(data => data));
  }

  /**
   * Update trainer image - TRN-11
   */
  updateImage(imageId: number, payload: { caption?: string; position?: number }) {
    return this.endpointService.trn_11(imageId, payload).pipe(map(data => data.data));
  }

  /**
   * Reorder trainer images - TRN-12
   */
  reorderImages(imageIds: number[]) {
    return this.endpointService.trn_12(imageIds).pipe(map(data => data.data));
  }

  // ==================== APPLICATION ENDPOINTS ====================

  /**
   * Submit trainer application - TRN-13
   */
  submitApplication(params: TRN_13) {
    return this.endpointService.trn_13(params).pipe(map(data => data.data));
  }

  /**
   * Get current user's trainer application - TRN-14
   */
  getApplication() {
    return this.endpointService.trn_14().pipe(map(data => data.data));
  }

  /**
   * Withdraw pending trainer application - TRN-15
   */
  withdrawApplication() {
    return this.endpointService.trn_15().pipe(map(data => data));
  }

  /**
   * Submit trainer recommendation - TRN-16
   */
  submitRecommendation(params: TRN_16) {
    return this.endpointService.trn_16(params).pipe(map(data => data.data));
  }

  // ==================== ADMIN ENDPOINTS ====================

  /**
   * Get all trainer applications (admin) - TRN-17
   */
  getApplications(params: { page?: number; per_page?: number; status?: string } = {}) {
    return this.endpointService.trn_17(params).pipe(map(data => data.data));
  }

  /**
   * Approve trainer application (admin) - TRN-18
   */
  approveApplication(applicationId: number) {
    return this.endpointService.trn_18(applicationId).pipe(map(data => data.data));
  }

  /**
   * Reject trainer application (admin) - TRN-19
   */
  rejectApplication(applicationId: number, rejectionReason: string) {
    return this.endpointService.trn_19(applicationId, { rejection_reason: rejectionReason }).pipe(map(data => data.data));
  }

  /**
   * Get all trainer recommendations (admin) - TRN-20
   */
  getRecommendations(params: { page?: number; per_page?: number; status?: string } = {}) {
    return this.endpointService.trn_20(params).pipe(map(data => data.data));
  }

  /**
   * Mark recommendation as reviewed (admin) - TRN-21
   */
  markRecommendationReviewed(recommendationId: number) {
    return this.endpointService.trn_21(recommendationId).pipe(map(data => data.data));
  }
}
